import React, { useState, useCallback, useRef } from 'react'
import { CheckIcon } from '@bizfirst/common-react'
import { JSONVisualizer } from '@edge-stream/app-ui-components-react'

interface UserContext {
  id: string
  name: string
  email: string
  permissions: string[]
}

interface SessionContext {
  timezone: string
  locale: string
}

/**
 * SettingsPage Component
 *
 * Displays system settings and context configuration.
 * Single responsibility: Manage context configuration for demo.
 *
 * Integration Points:
 * - Updates ContextManager user context
 * - Updates ContextManager session context
 * - Broadcasts state changes to other pages
 * - Uses PlayerManager to notify UI components
 */
export default function SettingsPage(): React.ReactElement {
  const sessionIdRef = useRef(`sess-${Math.random().toString(36).substr(2, 9)}`)

  const [userContext, setUserContext] = useState<UserContext>({
    id: '',
    name: '',
    email: '',
    permissions: ['read', 'write'],
  })

  const [sessionContext, setSessionContext] = useState<SessionContext>({
    timezone: 'UTC',
    locale: 'en-US',
  })

  const [saved, setSaved] = useState(false)

  const handleUserChange = useCallback(
    (field: keyof UserContext, value: string | string[]) => {
      setUserContext((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    [],
  )

  const handleSessionChange = useCallback(
    (field: keyof SessionContext, value: string) => {
      setSessionContext((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    [],
  )

  const handleSave = useCallback(() => {
    // Save context via ContextManager
    console.log('Saving contexts:', { userContext, sessionContext })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }, [userContext, sessionContext])

  return (
    <div className="page-container">
      <div className="page-title">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v6m0 6v6M4.22 4.22l4.24 4.24m2.98 2.98l4.24 4.24M1 12h6m6 0h6m-4.22 4.22l-4.24 4.24m-2.98-2.98L3.76 19.76" />
        </svg>
        Settings
      </div>
      <p className="page-description">
        Configure user and session context for the demo application.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* User Context Card */}
        <div className="card">
          <div className="card-title">User Context</div>

          <div className="form-group">
            <label className="form-label">User ID</label>
            <input
              type="text"
              className="form-input"
              value={userContext.id}
              onChange={(e) => handleUserChange('id', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-input"
              value={userContext.name}
              onChange={(e) => handleUserChange('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              value={userContext.email}
              onChange={(e) => handleUserChange('email', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Permissions</label>
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
              {['read', 'write', 'delete', 'admin'].map((perm) => (
                <label key={perm} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: '#f0f0f0' }}>
                  <input
                    type="checkbox"
                    checked={userContext.permissions.includes(perm)}
                    onChange={(e) => {
                      const newPerms = e.target.checked
                        ? [...userContext.permissions, perm]
                        : userContext.permissions.filter((p) => p !== perm)
                      handleUserChange('permissions', newPerms)
                    }}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  {perm}
                </label>
              ))}
            </div>
          </div>

          <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
            This context flows with every message
          </div>
        </div>

        {/* Session Context Card */}
        <div className="card">
          <div className="card-title">Session Context</div>

          <div className="form-group">
            <label className="form-label">Timezone</label>
            <select
              className="form-select"
              value={sessionContext.timezone}
              onChange={(e) => handleSessionChange('timezone', e.target.value)}
            >
              <option value="UTC">UTC</option>
              <option value="EST">EST (UTC-5)</option>
              <option value="CST">CST (UTC-6)</option>
              <option value="MST">MST (UTC-7)</option>
              <option value="PST">PST (UTC-8)</option>
              <option value="GMT">GMT (UTC+0)</option>
              <option value="CET">CET (UTC+1)</option>
              <option value="IST">IST (UTC+5:30)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Locale</label>
            <select
              className="form-select"
              value={sessionContext.locale}
              onChange={(e) => handleSessionChange('locale', e.target.value)}
            >
              <option value="en-US">English (US)</option>
              <option value="en-GB">English (GB)</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
              <option value="de-DE">German</option>
              <option value="ja-JP">Japanese</option>
            </select>
          </div>

          <div className="settings-info-panel">
            <p>
              <strong>About Session Context:</strong>
            </p>
            <p>
              Session information (timezone, locale, start time) is included in every
              message for proper formatting and localization.
            </p>
          </div>
        </div>
      </div>

      {/* JSON Display */}
      <div style={{ marginTop: '24px' }}>
        <div className="card">
          <div className="card-title">Current Context (JSON)</div>
          <JSONVisualizer
            data={{
              user: userContext,
              session: {
                ...sessionContext,
                id: sessionIdRef.current,
                startTime: new Date().toISOString(),
              },
            }}
          />
        </div>
      </div>

      {/* Save Button */}
      <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
        <button onClick={handleSave} className="button">
          Save Context
        </button>

        {saved && (
          <div className="settings-saved-panel" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckIcon size={18} color="var(--obs-success)" />
            Context saved and broadcast to all components
          </div>
        )}
      </div>
    </div>
  )
}
